import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextInput, Button, Loader, Alert } from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconUpload,
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconSparkles,
  IconRocket,
  IconUsers,
  IconCube,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useCourses,
  useMyEnrollments,
  useImportScorm,
  useLearningStats,
  useLearningTracks,
} from '../../hooks/useApi';
import { LearningTrack as LearningTrackType } from '../../types';
import { usePermission } from '../../hooks/usePermission';
import { getErrorMessage } from '../../utils/api';
import { Course, Enrollment } from '../../types';

type FilterValue = 'all' | 'my' | 'in_progress' | 'completed' | 'forMyGrade';

// Палитра-фоллбэк для обложек курсов, у которых ещё не задан coverGradient
// (старые записи). Новые курсы хранят свой градиент в `course.coverGradient`.
const COVER_PALETTE: Array<{ from: string; to: string }> = [
  { from: '#0A6B5A', to: '#12B886' },
  { from: '#0B5394', to: '#228BE6' },
  { from: '#3D2461', to: '#7048E8' },
  { from: '#0B4E72', to: '#228BE6' },
  { from: '#7C2D12', to: '#F08C00' },
  { from: '#1E3A8A', to: '#3B82F6' },
  { from: '#7F1D1D', to: '#E03131' },
  { from: '#5B21B6', to: '#BE4BDB' },
];

const hashIdx = (id: string, mod: number): number => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h) % mod;
};

const NEW_THRESHOLD_DAYS = 14;

// Описание курса хранится как HTML (из RichTextEditor). В карточке-превью
// показываем plain text, чтобы теги не торчали и line-clamp работал по тексту.
const HTML_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};
const stripHtml = (html?: string): string => {
  if (!html) return '';
  const noTags = html.replace(/<[^>]*>/g, ' ');
  const decoded = noTags.replace(/&[a-z#0-9]+;/gi, (m) => HTML_ENTITIES[m.toLowerCase()] ?? m);
  return decoded.replace(/\s+/g, ' ').trim();
};

// ────────────────────────────────────────────────────────────────────────────
// Stat strip
// ────────────────────────────────────────────────────────────────────────────
interface StatTile {
  key: string;
  label: string;
  value: number;
  sub: string;
  tone: 'accent' | 'warn' | 'neutral';
  unit?: string;
}

const StatStrip: React.FC<{ stats: StatTile[] }> = ({ stats }) => (
  <div className="learn-stats">
    {stats.map((s) => (
      <div key={s.key} className="learn-card learn-stat">
        <div className="learn-stat-head">
          <span className="learn-eyebrow">{s.label}</span>
          <span className={`learn-dot learn-dot-${s.tone}`} />
        </div>
        <div className="learn-stat-value">
          <span className="num">{s.value}</span>
          {s.unit && <span className="learn-stat-unit">{s.unit}</span>}
        </div>
        <div className="learn-stat-sub">{s.sub}</div>
      </div>
    ))}
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Tracks
// ────────────────────────────────────────────────────────────────────────────
const TrackIcon: React.FC<{ kind: 'rocket' | 'users' | 'cube' }> = ({ kind }) => {
  if (kind === 'rocket') return <IconRocket size={20} />;
  if (kind === 'users') return <IconUsers size={20} />;
  return <IconCube size={20} />;
};

const TrackCard: React.FC<{ track: LearningTrackType }> = ({ track }) => {
  const { t } = useTranslation();
  const total = track.courses;
  const pct = total > 0 ? track.completed / total : 0;
  const isDone = total > 0 && track.completed === total;
  return (
    <div className="learn-card learn-track">
      <div className="learn-track-head">
        <div
          className="learn-track-icon"
          style={{ background: `${track.color}22`, color: track.color }}
        >
          <TrackIcon kind={track.icon} />
        </div>
        {isDone && (
          <span className="learn-pill learn-pill-accent">
            <IconCheck size={11} /> {t('learning.trackDone')}
          </span>
        )}
      </div>
      <h3 className="learn-track-title">{track.title}</h3>
      <div className="learn-track-meta num">
        {t('learning.trackProgress', { completed: track.completed, total })}
      </div>
      <div className="learn-progress">
        <div className="learn-progress-fill" style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
};

const TracksRow: React.FC<{ tracks: LearningTrackType[]; onManage?: () => void }> = ({
  tracks,
  onManage,
}) => {
  const { t } = useTranslation();
  if (tracks.length === 0) return null;
  return (
    <div>
      <div className="learn-section-head">
        <div>
          <div className="learn-eyebrow">{t('learning.tracksEyebrow')}</div>
          <h2 className="learn-section-title">{t('learning.tracksTitle')}</h2>
        </div>
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className="learn-section-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('learning.tracksManage')}
          </button>
        )}
      </div>
      <div className="learn-tracks">
        {tracks.map((tr) => (
          <TrackCard key={tr._id} track={tr} />
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Filter bar
// ────────────────────────────────────────────────────────────────────────────
interface Tab {
  value: FilterValue;
  label: string;
}

const FilterBar: React.FC<{
  filter: FilterValue;
  onChange: (v: FilterValue) => void;
  counts: Record<FilterValue, number>;
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder: string;
  tabs: Tab[];
}> = ({ filter, onChange, counts, search, onSearchChange, searchPlaceholder, tabs }) => (
  <div className="learn-filter-bar">
    <div className="learn-tabs">
      {tabs.map((tab) => {
        const isActive = tab.value === filter;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={`learn-tab${isActive ? ' learn-tab-active' : ''}`}
          >
            {tab.label}
            <span className="num learn-tab-count">{counts[tab.value]}</span>
          </button>
        );
      })}
    </div>
    <TextInput
      placeholder={searchPlaceholder}
      leftSection={<IconSearch size={14} />}
      value={search}
      onChange={(e) => onSearchChange(e.currentTarget.value)}
      className="learn-search"
      size="sm"
    />
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Course card
// ────────────────────────────────────────────────────────────────────────────
interface UiCourse {
  _id: string;
  title: string;
  description?: string;
  type: 'custom' | 'scorm';
  modules: number;
  durationMin?: number;
  authorName?: string;
  primarySkill?: string;
  status: 'enrolled' | 'in_progress' | 'completed' | null;
  progress: number;
  forMyGrade: boolean;
  isNew: boolean;
  cover: { from: string; to: string };
}

const CourseCover: React.FC<{ course: UiCourse }> = ({ course }) => (
  <div
    className="learn-cover"
    style={{
      background: `linear-gradient(135deg, ${course.cover.from} 0%, ${course.cover.to} 100%)`,
    }}
  >
    <svg className="learn-cover-pattern" aria-hidden>
      <defs>
        <pattern
          id={`cover-dots-${course._id}`}
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(20)"
        >
          <circle cx="2" cy="2" r="1" fill="#fff" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#cover-dots-${course._id})`} />
    </svg>
    {course.primarySkill && <div className="learn-cover-skill">{course.primarySkill}</div>}
    {course.authorName && <div className="learn-cover-author">{course.authorName}</div>}
  </div>
);

const formatDuration = (min?: number): string => {
  if (!min || min <= 0) return '—';
  if (min < 60) return `${min}м`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}ч` : `${h}ч ${m}м`;
};

const CourseCard: React.FC<{ course: UiCourse; onOpen: () => void }> = ({ course, onOpen }) => {
  const { t } = useTranslation();
  const statusMap: Record<NonNullable<UiCourse['status']>, { label: string; tone: string }> = {
    in_progress: { label: t('learning.statusInProgress'), tone: 'warn' },
    completed: { label: t('learning.statusCompleted'), tone: 'accent' },
    enrolled: { label: t('learning.statusEnrolled'), tone: 'neutral' },
  };
  const st = course.status ? statusMap[course.status] : null;

  return (
    <button type="button" onClick={onOpen} className="learn-course">
      <CourseCover course={course} />
      <div className="learn-course-body">
        <div className="learn-course-pills">
          <span
            className={`learn-pill ${course.type === 'scorm' ? 'learn-pill-neutral' : 'learn-pill-ghost'}`}
          >
            {course.type === 'scorm'
              ? t('learning.courseTypeScorm')
              : t('learning.courseTypeCustom')}
          </span>
          {course.forMyGrade && (
            <span className="learn-pill learn-pill-accent">
              <IconSparkles size={10} /> {t('learning.courseForGrade')}
            </span>
          )}
          {course.isNew && (
            <span className="learn-pill learn-pill-warn">{t('learning.courseNew')}</span>
          )}
        </div>
        <h3 className="learn-course-title">{course.title}</h3>
        {course.description && <p className="learn-course-desc">{course.description}</p>}
        <div className="learn-course-meta">
          <span className="learn-meta-item">
            <IconClock size={11} />{' '}
            <span className="num">{formatDuration(course.durationMin)}</span>
          </span>
          <span className="learn-dot learn-dot-mini" />
          <span className="num">{t('learning.modulesCount', { count: course.modules })}</span>
        </div>
        {st && (
          <div className="learn-course-status">
            <div className="learn-course-status-head">
              <span className={`learn-pill learn-pill-${st.tone}`}>{st.label}</span>
              <span className="num learn-course-pct">
                {Math.round(course.progress * 100)}
                <span className="learn-course-pct-sym">%</span>
              </span>
            </div>
            <div className="learn-progress">
              <div className="learn-progress-fill" style={{ width: `${course.progress * 100}%` }} />
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Mapping API → UI
// ────────────────────────────────────────────────────────────────────────────
const buildUiCourse = (
  course: Course,
  enrollment: Enrollment | undefined,
  nextGradeCompetencyIds: Set<string>,
): UiCourse => {
  const status: UiCourse['status'] =
    enrollment?.status === 'in_progress'
      ? 'in_progress'
      : enrollment?.status === 'completed'
        ? 'completed'
        : enrollment?.status === 'enrolled'
          ? 'enrolled'
          : null;
  const progress = enrollment?.progress ?? 0;
  const createdAt = course.createdAt ? new Date(course.createdAt) : null;
  const isNew = createdAt
    ? Date.now() - createdAt.getTime() < NEW_THRESHOLD_DAYS * 24 * 60 * 60 * 1000
    : false;
  const idx = hashIdx(course._id, COVER_PALETTE.length);
  const cover = course.coverGradient
    ? { from: course.coverGradient.from, to: course.coverGradient.to }
    : COVER_PALETTE[idx];
  const totalDuration = (course.modules || []).reduce((sum, m) => sum + (m.durationMin || 0), 0);
  const primarySkill = (course.competencyIds || [])
    .map((ref) => (typeof ref === 'string' ? undefined : ref.title))
    .find((v): v is string => !!v);
  return {
    _id: course._id,
    title: course.title,
    description: stripHtml(course.description),
    type: course.type,
    modules: course.modules?.length ?? 0,
    durationMin: totalDuration > 0 ? totalDuration : undefined,
    authorName: course.authorId?.name,
    primarySkill,
    status,
    progress: status ? progress / 100 : 0,
    // Честная проверка: курс «под мой грейд», если его компетенции пересекаются
    // с теми, что нужны для следующего грейда юзера. Если у юзера нет грейда
    // или next-grade не определён — фильтр будет давать пустой результат, и
    // это правильно (показывать все курсы как «под мой грейд» нечестно).
    forMyGrade: (course.competencyIds || []).some((ref) => {
      const id = typeof ref === 'string' ? ref : ref._id;
      return nextGradeCompetencyIds.has(id);
    }),
    isNew,
    cover,
  };
};

// ────────────────────────────────────────────────────────────────────────────
// App
// ────────────────────────────────────────────────────────────────────────────
export const LearningCatalog: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Управление курсами — право learning.manage с сервера, а не флаг admin.
  const isLearningManager = usePermission('learning.manage');

  const { data: courses = [], isLoading: isCoursesLoading, error: coursesError } = useCourses();
  const { data: enrollments = [], isLoading: isEnrollmentsLoading } = useMyEnrollments();
  const { data: learningStats } = useLearningStats();
  const { data: tracks = [] } = useLearningTracks();
  const importScorm = useImportScorm();

  const [filter, setFilter] = useState<FilterValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const enrollmentMap = useMemo(() => {
    const map = new Map<string, Enrollment>();
    enrollments.forEach((e) => {
      const id = typeof e.courseId === 'string' ? e.courseId : e.courseId._id;
      map.set(id, e);
    });
    return map;
  }, [enrollments]);

  const nextGradeCompetencySet = useMemo(
    () => new Set(learningStats?.nextGradeCompetencyIds || []),
    [learningStats?.nextGradeCompetencyIds],
  );

  const uiCourses = useMemo<UiCourse[]>(
    () =>
      (courses as Course[]).map((c) =>
        buildUiCourse(c, enrollmentMap.get(c._id), nextGradeCompetencySet),
      ),
    [courses, enrollmentMap, nextGradeCompetencySet],
  );

  const counts = useMemo<Record<FilterValue, number>>(
    () => ({
      all: uiCourses.length,
      my: uiCourses.filter((c) => c.status !== null).length,
      in_progress: uiCourses.filter((c) => c.status === 'in_progress').length,
      completed: uiCourses.filter((c) => c.status === 'completed').length,
      forMyGrade: uiCourses.filter((c) => c.forMyGrade).length,
    }),
    [uiCourses],
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return uiCourses.filter((c) => {
      if (filter === 'my' && c.status === null) return false;
      if (filter === 'in_progress' && c.status !== 'in_progress') return false;
      if (filter === 'completed' && c.status !== 'completed') return false;
      if (filter === 'forMyGrade' && !c.forMyGrade) return false;
      if (
        q &&
        !c.title.toLowerCase().includes(q) &&
        !(c.description || '').toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [uiCourses, filter, searchQuery]);

  // Серверные числа имеют приоритет (актуальны для всех записей), но пока хук
  // не вернул — падаем на локальный подсчёт по уже подгруженным enrollments.
  const inProgress = learningStats?.inProgress ?? counts.in_progress;
  const enrolledTotal = learningStats?.enrolled ?? counts.my;
  const completedTotal = learningStats?.completed ?? counts.completed;
  // Полчаса — это 0,5 ч, а не 1 ч: одна цифра после запятой.
  const hoursTotal = Math.round((learningStats?.hoursMinutes ?? 0) / 6) / 10;

  const stats: StatTile[] = useMemo(
    () => [
      {
        key: 'inProgress',
        label: t('learning.statInProgress'),
        value: inProgress,
        sub: t('learning.statInProgressSub'),
        tone: 'accent',
      },
      {
        key: 'enrolled',
        label: t('learning.statEnrolled'),
        value: enrolledTotal,
        sub: t('learning.statEnrolledSub'),
        tone: 'neutral',
      },
      {
        key: 'completed',
        label: t('learning.statCompleted'),
        value: completedTotal,
        sub: t('learning.statCompletedSub'),
        tone: 'accent',
      },
      {
        key: 'hours',
        label: t('learning.statHours'),
        value: hoursTotal,
        sub: t('learning.statHoursSub'),
        tone: 'warn',
        unit: t('learning.statHoursUnit'),
      },
    ],
    [inProgress, enrolledTotal, completedTotal, hoursTotal, t],
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importScorm.mutate(file, {
        onError: (err) =>
          notifications.show({
            message: getErrorMessage(err, t('common.error')),
            color: 'red',
          }),
      });
    }
    event.target.value = '';
  };

  if (coursesError) {
    return (
      <div className="learning-page" style={{ padding: 32 }}>
        <Alert icon={<IconAlertCircle size={16} />} title={t('messages.error')} color="red">
          {t('messages.errorLoading', { item: t('learning.title') })}
        </Alert>
      </div>
    );
  }

  const isLoading = isCoursesLoading || isEnrollmentsLoading;
  const coursesCountLabel = t('learning.courses', { count: filtered.length });

  return (
    <div className="learning-page">
      <style>{`
        .learning-page {
          --learn-bg: var(--mantine-color-body);
          --learn-surface: var(--mantine-color-body);
          --learn-surface-2: var(--mantine-color-default-hover);
          --learn-line: var(--mantine-color-default-border);
          --learn-line-strong: var(--mantine-color-default-border);
          --learn-ink: var(--mantine-color-text);
          --learn-ink-2: var(--mantine-color-text);
          --learn-muted: var(--mantine-color-dimmed);
          --learn-soft: var(--mantine-color-placeholder);
          --learn-accent: var(--mantine-primary-color-filled);
          --learn-accent-soft: var(--mantine-primary-color-light);
          --learn-accent-deep: var(--mantine-primary-color-light-color);
          --learn-warn: #F08C00;
          color: var(--learn-ink);
          padding: 32px 40px 60px;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        .learning-page .num { font-variant-numeric: tabular-nums; }
        .learning-page h1, .learning-page h2, .learning-page h3 { margin: 0; font-weight: 600; color: var(--learn-ink); }

        .learn-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
        .learn-hero h1 { font-size: 30px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; margin-bottom: 6px; }
        .learn-hero p { font-size: 14px; color: var(--learn-muted); margin: 0; }
        .learn-eyebrow { font-size: 12px; color: var(--learn-muted); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 600; }
        .learn-hero-actions { display: flex; gap: 8px; }

        .learn-card {
          background: var(--learn-surface);
          border: 1px solid var(--learn-line);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03);
        }

        .learn-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 900px) { .learn-stats { grid-template-columns: repeat(2, 1fr); } }
        .learn-stat { padding: 20px; }
        .learn-stat-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
        .learn-stat-value { display: flex; align-items: baseline; gap: 6px; font-size: 30px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; }
        .learn-stat-unit { font-size: 14px; color: var(--learn-muted); font-weight: 500; }
        .learn-stat-sub { font-size: 12px; color: var(--learn-muted); margin-top: 6px; }

        .learn-dot { width: 7px; height: 7px; border-radius: 999px; display: inline-block; }
        .learn-dot-accent { background: var(--learn-accent); }
        .learn-dot-warn { background: var(--learn-warn); }
        .learn-dot-neutral { background: var(--learn-muted); }
        .learn-dot-mini { width: 3px; height: 3px; background: var(--learn-soft); }

        .learn-section-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
        .learn-section-title { font-size: 18px; font-weight: 600; letter-spacing: -0.005em; margin-top: 2px; }
        .learn-section-link { font-size: 12px; color: var(--learn-accent-deep); font-weight: 500; display: inline-flex; align-items: center; gap: 4px; text-decoration: none; }

        .learn-tracks { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        @media (max-width: 900px) { .learn-tracks { grid-template-columns: 1fr; } }
        .learn-track { padding: 20px; transition: box-shadow 160ms, transform 160ms; cursor: pointer; }
        .learn-track:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04); transform: translateY(-1px); }
        .learn-track-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .learn-track-icon { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .learn-track-title { font-size: 15px; font-weight: 600; margin-bottom: 6px; }
        .learn-track-meta { font-size: 12px; color: var(--learn-muted); margin-bottom: 10px; }

        .learn-progress { height: 4px; background: var(--learn-surface-2); border-radius: 999px; overflow: hidden; }
        .learn-progress-fill { height: 100%; background: var(--learn-accent); transition: width 200ms ease; }

        .learn-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px; line-height: 1; }
        .learn-pill-accent { background: var(--learn-accent-soft); color: var(--learn-accent-deep); }
        .learn-pill-warn { background: rgba(240, 140, 0, 0.12); color: var(--learn-warn); }
        .learn-pill-neutral { background: var(--learn-surface-2); color: var(--learn-ink-2); }
        .learn-pill-ghost { background: transparent; color: var(--learn-muted); border: 1px solid var(--learn-line); }

        .learn-filter-bar { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
        .learn-tabs { display: inline-flex; gap: 4px; padding: 4px; background: var(--learn-surface); border: 1px solid var(--learn-line); border-radius: 10px; }
        .learn-tab { padding: 7px 14px; font-size: 13px; font-weight: 500; border: none; border-radius: 7px; background: transparent; color: var(--learn-muted); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
        .learn-tab-active { background: var(--learn-accent-soft); color: var(--learn-accent-deep); }
        .learn-tab-count { font-size: 11px; opacity: 0.7; }
        .learn-search { min-width: 280px; }

        .learn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 16px; }

        .learn-course {
          background: var(--learn-surface);
          border: 1px solid var(--learn-line);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03);
          transition: box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease;
          cursor: pointer;
          text-align: left;
          padding: 0;
          font: inherit;
          color: inherit;
          display: flex;
          flex-direction: column;
        }
        .learn-course:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04); border-color: var(--learn-line-strong); transform: translateY(-2px); }

        .learn-cover { position: relative; height: 130px; overflow: hidden; }
        .learn-cover-pattern { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0.25; pointer-events: none; }
        .learn-cover-skill { position: absolute; bottom: 14px; left: 16px; color: #fff; font-size: 11px; font-weight: 600; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.14em; }
        .learn-cover-author { position: absolute; top: 14px; right: 16px; color: #fff; font-size: 11px; font-weight: 500; background: rgba(0,0,0,0.18); padding: 3px 8px; border-radius: 999px; backdrop-filter: blur(4px); }

        .learn-course-body { padding: 18px; display: flex; flex-direction: column; flex: 1; }
        .learn-course-pills { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
        .learn-course-title { font-size: 15px; font-weight: 600; line-height: 1.35; margin-bottom: 8px; letter-spacing: -0.005em; }
        .learn-course-desc { font-size: 13px; color: var(--learn-muted); line-height: 1.5; margin: 0 0 12px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .learn-course-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--learn-muted); margin-top: auto; flex-wrap: wrap; }
        .learn-meta-item { display: inline-flex; align-items: center; gap: 4px; }
        .learn-course-status { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--learn-line); }
        .learn-course-status-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
        .learn-course-pct { font-size: 12px; font-weight: 600; }
        .learn-course-pct-sym { font-size: 10px; color: var(--learn-muted); font-weight: 500; }

        .learn-empty { padding: 48px; text-align: center; color: var(--learn-muted); font-size: 14px; }
      `}</style>

      <div className="learn-hero">
        <div>
          <div className="learn-eyebrow" style={{ marginBottom: 8 }}>
            {t('learning.title')}
          </div>
          <h1>{t('learning.catalog')}</h1>
          <p>{t('learning.heroSubtitle')}</p>
        </div>
        {isLearningManager && (
          <div className="learn-hero-actions">
            <Button
              variant="default"
              leftSection={<IconRocket size={14} />}
              onClick={() => navigate('/learning/tracks')}
            >
              {t('learning.tracksAdminBtn')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconUpload size={14} />}
              loading={importScorm.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('learning.importScorm')}
            </Button>
            <Button
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate('/learning/new/edit')}
            >
              {t('learning.createCourse')}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".zip"
              onChange={handleFileSelect}
            />
          </div>
        )}
      </div>

      <StatStrip stats={stats} />

      <TracksRow
        tracks={tracks}
        onManage={isLearningManager ? () => navigate('/learning/tracks') : undefined}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="learn-section-head">
          <div>
            <div className="learn-eyebrow">{t('learning.catalogEyebrow')}</div>
            <h2 className="learn-section-title">{coursesCountLabel}</h2>
          </div>
        </div>

        <FilterBar
          filter={filter}
          onChange={setFilter}
          counts={counts}
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={t('learning.searchPlaceholder')}
          tabs={[
            { value: 'all', label: t('common.all') },
            { value: 'my', label: t('learning.myCourses') },
            { value: 'in_progress', label: t('learning.tabInProgress') },
            { value: 'completed', label: t('learning.completed') },
            { value: 'forMyGrade', label: t('learning.forMyGrade') },
          ]}
        />

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Loader size="sm" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="learn-card learn-empty">{t('learning.emptyForFilter')}</div>
        ) : (
          <div className="learn-grid">
            {filtered.map((c) => (
              <CourseCard key={c._id} course={c} onOpen={() => navigate(`/learning/${c._id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
